import type { YaasPositionCardData } from "@solomon-apps/shared/src/types/yaas";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";

import type { RaydiumLpHolding } from "./lpTypes";
import { fetchRaydiumLpHoldings, unmatchedLpHoldings } from "./raydiumLpHoldings";

export function useRaydiumLpHoldings(
    walletAddress: string | null | undefined,
    positions: YaasPositionCardData[],
    enabled: boolean
): {
    holdings: RaydiumLpHolding[];
    incomplete: boolean;
    error: Error | null;
    isFetching: boolean;
    refetch: () => Promise<void>;
} {
    const active = enabled && Boolean(walletAddress);
    const query = useQuery({
        queryKey: ["yaas", "raydium-lp-holdings", walletAddress],
        queryFn: ({ signal }): Promise<RaydiumLpHolding[]> => {
            if (!walletAddress) throw new Error("No wallet for LP discovery");
            return fetchRaydiumLpHoldings(walletAddress, signal);
        },
        enabled: active,
        staleTime: 5 * 60_000,
        refetchInterval: 5 * 60_000,
        retry: 1
    });
    // The UI shows a plain "Failed to fetch data.", so the cause
    // (timeout, rate limit, a lagging RPC node) is only visible here.
    useEffect(() => {
        if (query.error) console.warn("Raydium LP discovery failed:", query.error);
    }, [query.error]);
    const refetch = async (): Promise<void> => {
        if (active) await query.refetch();
    };
    if (!active) return { holdings: [], incomplete: false, error: null, isFetching: false, refetch };
    const holdings = unmatchedLpHoldings(query.data ?? [], positions);
    return {
        // Failed refreshes retain the last successful snapshot, including an empty one.
        holdings,
        incomplete: query.data === undefined,
        error: query.data === undefined ? query.error : null,
        isFetching: query.isFetching,
        refetch
    };
}
